<!-- A compact LCD — no device body — showing one page the way the chosen
     device draws it. Compose uses it for the live preview.

     `ticker` (optional) runs a line across the top of the glass: Compose shows
     an arriving page there so a draft is never interrupted. It lies over the
     status row, so it never makes the glass taller.

     FIXED GEOMETRY, like the pager itself: status row + a message window of the
     device's rows + the hint row, whatever the draft's length. A draft longer
     than the window keeps its last rows in view — the line being typed. -->
<template>
  <figure class="pm-lcd-preview">
    <figcaption class="sr-only">Preview on {{ device.name }}</figcaption>
    <Lcd :kind="device.lcd" :font="device.font" compact :style="geometry">
      <div v-if="ticker" class="pm-lcd-ticker pm-lcd-s" aria-hidden="true">
        <span class="pm-lcd-ticker__run" data-motion>{{ ticker }}</span>
      </div>
      <div class="pm-lcd-status pm-lcd-s">
        <span class="pm-lcd-status__from">{{ fromLine }}</span>
        <span class="pm-lcd-status__time">{{ now }}</span>
      </div>
      <div ref="win" class="pm-lcd-win">
        <div v-if="!body" class="pm-lcd-l pm-lcd-placeholder">WHAT'S UP</div>
        <div v-else-if="segDigits" class="pm-lcd-seg pm-lcd-l">
          <span class="pm-lcd-seg__ghost" aria-hidden="true">{{ ghost }}</span>
          <span class="pm-lcd-seg__ink">{{ segDigits }}</span>
        </div>
        <div v-else class="pm-lcd-l pm-lcd-body" :class="{ 'pm-lcd-body--seg': segMixed }">
          <PageText :body="body" />
        </div>
      </div>
      <div class="pm-lcd-s pm-lcd-hint">{{ hint }}</div>
    </Lcd>
  </figure>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import Lcd from './Lcd.vue';
import PageText from './PageText.vue';
import { deviceById } from '../pm/devices.js';
import { hintFor } from '../pm/codebook.js';
import { formatTime } from '../pm/format.js';
import { segForm, isSegMixed, hintLine } from './Lcd.vue';

const props = defineProps({
  deviceId: { type: String, default: 'buzz12' },
  body: { type: String, default: '' },
  from: { type: String, default: '' },
  ticker: { type: String, default: '' },
});

const device = computed(() => deviceById(props.deviceId) || deviceById('buzz12'));
const numeric = computed(() => device.value?.family === 'numeric');
const segDigits = computed(() => (numeric.value ? segForm(props.body, device.value.cols) : ''));
const segMixed = computed(() => numeric.value && !segDigits.value && isSegMixed(props.body));
const ghost = computed(() => '8'.repeat(Math.max(segDigits.value.length, 1)));
const hint = computed(() => hintLine(props.body, hintFor(props.body)));
const fromLine = computed(() => (props.from ? `FROM ${props.from}`.toUpperCase() : 'PAGE'));
const now = formatTime(Date.now());

// Message-window rows per device — the same as the pager screen (device.css).
const WINDOW = { buzz12: [3, 1.25], topaz: [1, 1.12], memo: [4, 1.12] };
const geometry = computed(() => {
  const [rows, k] = WINDOW[device.value.id] || [3, 1.12];
  return { '--win-rows': rows, '--row-k': k, '--cols': device.value.cols };
});

// Keep the end of the draft in view as it grows past the window.
const win = ref(null);
watch(() => [props.body, props.deviceId], () => nextTick(() => {
  const w = win.value;
  if (w) w.scrollTop = w.scrollHeight;
}), { immediate: true });
</script>
